/**
 * PaymentRequestAttachments.tsx
 *
 * Reusable Attachments block for the Payment Request module. Used on:
 *   - Create wizard (after first save) — initiator uploads invoice / payment
 *     certificate / supporting docs.
 *   - Detail page — visible to all viewers; AP team adds bank-confirmation /
 *     other docs at the AP-prep stage; admins can delete via override.
 *
 * Spec ref: docs/superpowers/specs/2026-04-25-payment-request-design.md § 4.1
 * (PaymentRequestAttachments table) and § 7.3 (attachments endpoints).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    CircularProgress,
    FormControl,
    IconButton,
    InputLabel,
    Link as MuiLink,
    List,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    MenuItem,
    Select,
    Stack,
    Typography,
    Alert,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import apiService from '../../services/api.service';

// ───────────────────────────────────────────────────────────────────────────
// Types

interface AttachmentRow {
    attachmentid: number;
    originalfilename: string;
    mimetype: string;
    filesizebytes: number;
    attachmenttype: string;
    uploadedbyuserid: number;
    uploadedat: string;
    uploadedatstage: string;
}

const ATTACHMENT_TYPES = [
    'Invoice',
    'PaymentCertificate',
    'PO',
    'GRPO',
    'BankConfirmation',
    'Other',
] as const;

const STAGE_COLOR: Record<string, 'default' | 'primary' | 'success' | 'info' | 'warning'> = {
    Initiator: 'primary',
    AP: 'info',
    CA: 'info',
    FM: 'info',
    GM: 'info',
    CEO: 'info',
    System: 'default',
};

function formatBytes(n: number): string {
    if (!Number.isFinite(n) || n <= 0) return '—';
    const units = ['B', 'KB', 'MB', 'GB'];
    let i = 0;
    let v = n;
    while (v >= 1024 && i < units.length - 1) {
        v /= 1024;
        i += 1;
    }
    return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

function formatDateTime(iso?: string): string {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
}

// ───────────────────────────────────────────────────────────────────────────
// Component

export interface PaymentRequestAttachmentsProps {
    /** The Payment Request ID. When missing (i.e. Create form before first save), the block renders disabled with a hint. */
    paymentRequestId?: number | null;
    /** True if the current user can upload + delete in this state. */
    canEdit: boolean;
    /** What to tag uploads with — e.g. 'Initiator' on Create, 'AP' on AP-prep. */
    defaultStage: 'Initiator' | 'AP' | 'CA' | 'FM' | 'GM' | 'CEO' | 'System';
    /** Called after a successful upload or delete so the parent can refresh related state. */
    onChange?: () => void;
}

export default function PaymentRequestAttachments({
    paymentRequestId,
    canEdit,
    defaultStage,
    onChange,
}: PaymentRequestAttachmentsProps) {
    const [items, setItems] = useState<AttachmentRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pickedType, setPickedType] = useState<string>('Other');
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const sortedItems = useMemo(
        () => [...items].sort((a, b) => (b.uploadedat ?? '').localeCompare(a.uploadedat ?? '')),
        [items],
    );

    const reload = async () => {
        if (!paymentRequestId) return;
        setLoading(true);
        setError(null);
        try {
            const res = await apiService.listPaymentRequestAttachments(paymentRequestId);
            setItems(Array.isArray(res?.data) ? res.data : []);
        } catch (e: any) {
            setError(e?.response?.data?.message || e?.message || 'Failed to load attachments');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (paymentRequestId) {
            reload();
        } else {
            setItems([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentRequestId]);

    const handlePickFile = () => {
        if (!canEdit || !paymentRequestId) return;
        fileInputRef.current?.click();
    };

    const handleFileChosen = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (event.target) event.target.value = '';
        if (!file || !paymentRequestId) return;
        setBusy(true);
        setError(null);
        try {
            await apiService.uploadPaymentRequestAttachment(
                paymentRequestId,
                file,
                pickedType,
                defaultStage,
            );
            await reload();
            if (onChange) onChange();
        } catch (e: any) {
            setError(e?.response?.data?.message || e?.message || 'Upload failed');
        } finally {
            setBusy(false);
        }
    };

    const handleDownload = async (row: AttachmentRow) => {
        if (!paymentRequestId) return;
        try {
            await apiService.downloadPaymentRequestAttachment(
                paymentRequestId,
                row.attachmentid,
                row.originalfilename,
                row.mimetype,
            );
        } catch (e: any) {
            setError(e?.response?.data?.message || e?.message || 'Download failed');
        }
    };

    const handleDelete = async (row: AttachmentRow) => {
        if (!paymentRequestId) return;
        if (!window.confirm(`Delete "${row.originalfilename}"?`)) return;
        setBusy(true);
        setError(null);
        try {
            await apiService.deletePaymentRequestAttachment(paymentRequestId, row.attachmentid);
            await reload();
            if (onChange) onChange();
        } catch (e: any) {
            setError(e?.response?.data?.message || e?.message || 'Delete failed');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card variant="outlined">
            <CardHeader
                title="Attachments"
                titleTypographyProps={{ variant: 'h6' }}
                subheader="Invoice, Payment Certificate, Bank Confirmation, and supporting documents"
            />
            <CardContent>
                {!paymentRequestId && (
                    <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                        Save the request as Draft first to start adding attachments.
                    </Alert>
                )}

                {error && (
                    <Alert severity="error" variant="outlined" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {canEdit && paymentRequestId && (
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                        sx={{ mb: 2 }}
                    >
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel id="att-type">Type</InputLabel>
                            <Select
                                labelId="att-type"
                                label="Type"
                                value={pickedType}
                                onChange={(e) => setPickedType(String(e.target.value))}
                                disabled={busy}
                            >
                                {ATTACHMENT_TYPES.map((t) => (
                                    <MenuItem key={t} value={t}>
                                        {t}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<CloudUploadIcon />}
                            onClick={handlePickFile}
                            disabled={busy}
                        >
                            {busy ? 'Uploading…' : 'Upload file'}
                        </Button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            hidden
                            onChange={handleFileChosen}
                            accept=".pdf,.png,.jpg,.jpeg,.gif,.tiff,.bmp,.docx,.doc,.xlsx,.xls,.csv,.txt"
                        />
                    </Stack>
                )}

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={24} />
                    </Box>
                )}

                {!loading && sortedItems.length === 0 && paymentRequestId && (
                    <Typography variant="body2" color="text.secondary">
                        No attachments yet.
                    </Typography>
                )}

                {!loading && sortedItems.length > 0 && (
                    <List dense>
                        {sortedItems.map((row) => (
                            <ListItem key={row.attachmentid} divider>
                                <ListItemText
                                    primary={
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <MuiLink
                                                component="button"
                                                type="button"
                                                onClick={() => handleDownload(row)}
                                                underline="hover"
                                                sx={{ textAlign: 'left' }}
                                            >
                                                {row.originalfilename}
                                            </MuiLink>
                                            <Chip size="small" label={row.attachmenttype} />
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                label={row.uploadedatstage}
                                                color={STAGE_COLOR[row.uploadedatstage] || 'default'}
                                            />
                                        </Stack>
                                    }
                                    secondary={
                                        <Typography variant="caption" color="text.secondary">
                                            {formatBytes(row.filesizebytes)} · {row.mimetype} ·{' '}
                                            uploaded {formatDateTime(row.uploadedat)}
                                        </Typography>
                                    }
                                />
                                <ListItemSecondaryAction>
                                    <IconButton
                                        edge="end"
                                        onClick={() => handleDownload(row)}
                                        title="Download"
                                    >
                                        <DownloadIcon />
                                    </IconButton>
                                    {canEdit && (
                                        <IconButton
                                            edge="end"
                                            onClick={() => handleDelete(row)}
                                            disabled={busy}
                                            title="Delete"
                                            sx={{ ml: 1 }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    )}
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>
                )}
            </CardContent>
        </Card>
    );
}
